import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";

/**
 * Tabla a sangre completa. Sin contenedor redondeado, sin sombra.
 * Header hundido con etiquetas monoespaciadas mayúsculas. Filas de 36px
 * separadas por línea hairline. Al pasar el mouse: fondo apenas más
 * cálido + barra de acento de 2px en el borde izquierdo; la fila
 * seleccionada (isRowSelected) mantiene esa barra sin necesitar hover.
 */
export const DataTable = ({
  columns,
  rows,
  loading,
  total = 0,
  page = 1,
  pageSize = 25,
  onPageChange,
  sort,
  order = 1,
  onSortChange,
  onRowClick,
  isRowSelected,
  empty,
  testId = "data-table",
}) => {
  const { t } = useT();
  const pages = Math.max(1, Math.ceil(total / pageSize));

  if (loading) {
    return (
      <div className="px-6 py-4" data-testid={`${testId}-loading`}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="mb-[11px] h-[13px] w-full animate-pulse bg-surface-sunken" />
        ))}
      </div>
    );
  }

  if (!rows?.length) return empty || null;

  return (
    <div data-testid={testId} className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead className="sticky top-0 z-10 bg-surface-sunken">
            <tr className="h-[32px] border-b border-line-strong">
              {columns.map((col) => {
                const isSorted = sort === col.key;
                return (
                  <th
                    key={col.key}
                    style={{ width: col.width }}
                    className={cn(
                      "select-none px-3 text-left font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-ink-tertiary",
                      col.align === "right" && "text-right",
                      col.sortable && "cursor-pointer hover:text-ink"
                    )}
                    onClick={() => col.sortable && onSortChange?.(col.key)}
                    data-testid={`th-${col.key}`}
                  >
                    <span className={cn("inline-flex items-center gap-1.5", col.align === "right" && "justify-end")}>
                      {col.label}
                      {isSorted && (
                        <span
                          className={cn(
                            "inline-block h-0 w-0 border-x-[3px] border-x-transparent",
                            order === 1 ? "border-b-[4px] border-b-brand" : "border-t-[4px] border-t-brand"
                          )}
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const selected = !!isRowSelected?.(row);
              return (
                <tr
                  key={row.id || idx}
                  data-testid={`row-${row.id || idx}`}
                  onClick={() => onRowClick?.(row)}
                  className={cn(
                    "group h-[36px] border-b border-line bg-paper transition-colors duration-[120ms] hover:bg-surface-sunken",
                    onRowClick && "cursor-pointer"
                  )}
                >
                  {columns.map((col, ci) => (
                    <td
                      key={col.key}
                      className={cn(
                        "relative px-3 align-middle text-[13px] text-ink",
                        col.align === "right" && "text-right font-mono tabular-nums",
                        ci === 0 &&
                          cn(
                            "before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-brand before:transition-opacity before:duration-[120ms]",
                            selected ? "before:opacity-100" : "before:opacity-0 group-hover:before:opacity-100"
                          )
                      )}
                    >
                      {col.render ? col.render(row) : row[col.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-line bg-paper px-4 py-2 text-[12px] text-ink-secondary">
        <span data-testid={`${testId}-total`} className="font-mono">
          {total} {t("app.results")}
        </span>
        <div className="flex items-center gap-2">
          <span className="font-mono">
            {t("app.page")} {page} {t("app.of")} {pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-7 rounded-control p-0"
            disabled={page <= 1}
            onClick={() => onPageChange?.(page - 1)}
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-7 rounded-control p-0"
            disabled={page >= pages}
            onClick={() => onPageChange?.(page + 1)}
            data-testid={`${testId}-next`}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
